import {SimpleIcon} from "@client/components/Icon";
import {UserMarkdown} from "@client/components/UserMarkdown";
import * as constants from "@client/constants";
import {zIndexClasses} from "@client/style";
import * as utils from "@client/utils";
import * as shared from "@gesamtkunstwerk/shared/forms";
import {classNames} from "@reactivated";
import {Decimal} from "decimal.js";
import React from "react";

// Tailwind class mappings replacing vanilla-extract styles from widgets.css.ts
export const widgetStyles = {
    widgetCommon:
        "h-[35px] py-[5px] px-[10px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] focus:outline-none focus:border-[#D8D8D8] focus:shadow-none placeholder:leading-normal placeholder:text-[#999999]",
    small: "text-[11px]! [button&]:pl-3 [button&]:pr-3",
    label: "label space-y-2 text-sm",
    labelText: "font-semibold font-[InterVariable] text-[#181a1b]",
    input: "h-[35px] py-[5px] px-[10px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] focus:outline-none focus:border-[#D8D8D8] focus:shadow-none placeholder:leading-normal placeholder:text-[#999999] w-full read-only:bg-[#EFEFEF]",
    range: "h-[35px] py-[5px] px-[10px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] w-full h-5 focus:outline-none",
    flush: `bg-stone-50 block rounded-[19px] border-none shadow-none px-[45px] relative ${zIndexClasses.flush} text-[#373A3C]`,
    flushWrapper: "relative shrink basis-full",
    flushIcon: `flush-icon block absolute left-[14px] top-[9px] w-[18px] h-[18px] ${zIndexClasses.flushIcon}`,
    textarea:
        "h-[35px] py-[5px] px-[10px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] focus:outline-none focus:border-[#D8D8D8] focus:shadow-none placeholder:leading-normal placeholder:text-[#999999] w-full read-only:bg-[#EFEFEF] min-h-[200px]",
    selectWrapper: "relative",
    selectIcon: "select-icon hidden",
    select: "widget-select h-[35px] py-[5px] px-[10px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] focus:outline-none focus:border-[#D8D8D8] focus:shadow-none placeholder:leading-normal placeholder:text-[#999999] pr-8 w-full leading-[1.4] text-[13px]!",
    inputGroup: "input-group flex flex-row",
    inputGroupReadOnly: "input-group-read-only",
    inputGroupLabel:
        "h-[35px] py-[5px] px-[10px] block text-sm shrink bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] focus:outline-none focus:border-[#D8D8D8] focus:shadow-none placeholder:leading-normal placeholder:text-[#999999] basis-auto text-sm h-[35px] align-middle whitespace-nowrap leading-[22px]",
    checkboxIcon:
        "checkbox-icon absolute m-auto block inset-0 h-[38%] w-[38%] duration-300 pointer-events-none",
    checkboxStyle:
        "checkbox-style self-center shrink-0 basis-auto relative inline-block cursor-pointer",
    switchStyle: "switch-style",
    quickSelect: "flex bg-white",
    quickSelectOptionDisabled: "opacity-30 cursor-not-allowed",
    quickSelectOptionSelectedAndDisabled: "bg-[#FEFEFE] border border-[#CCC]",
    quickSelectReadOnly: "",
    quickSelectOption:
        "quick-select-option h-[35px] py-[5px] px-[7px] text-sm flex-1 leading-[22px] inline-block cursor-pointer border border-[#D8D8D8] border-r-0 whitespace-nowrap text-[#333] overflow-hidden text-ellipsis shrink basis-auto text-center",
    quickSelected: "opacity-100! shrink-0 bg-[#FBE8E1]",
    quickSelectEven: "quick-select-even",
    shrinkLast: "shrink-last",
    readOnly: "bg-[#EFEFEF]",
    readOnlyButStillInput: "bg-[#EFEFEF]",
    error: "field-error-state",
    FieldError: "font-medium font-[InterVariable] text-[rgb(230,32,32)] text-[11px]",
} as const;

// Keep backward-compatible namespace for internal use
const styles = widgetStyles;

interface FormOption<T> {
    value: T;
    label: string;
}

interface BaseBooleanProps {
    className?: string;
    onChange: (value: boolean) => void;
    value: boolean | string | null;
    name: string;
    readOnly?: boolean;
}

const BaseBoolean = (props: BaseBooleanProps) => {
    const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.onChange(event.currentTarget.checked);
    };

    return (
        <input
            name={props.name}
            className={props.className}
            aria-label="Toggle item"
            type="checkbox"
            onChange={handleChange}
            disabled={props.readOnly}
            checked={props.value != null && [true, "true", "on"].includes(props.value)}
        />
    );
};

interface CheckboxProps {
    name: string;
    onChange: (value: boolean) => void;
    value: boolean | null;
    readOnly?: boolean;
}

export const Checkbox = (props: CheckboxProps) => {
    return (
        <div className={styles.checkboxStyle}>
            <BaseBoolean {...props} />
            <SimpleIcon className={styles.checkboxIcon} icon="custom-bolder-check" />
        </div>
    );
};

type SwitchProps = CheckboxProps;

export const Switch = (props: SwitchProps) => {
    return <BaseBoolean {...props} className={styles.switchStyle} />;
};

interface DecimalInputProps {
    name: string;
    value: DecimalValue | null;
    onChange: {hack(value: DecimalValue | null): void}["hack"];
    hasError?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    className?: string;
    integerOnly?: boolean;
    autoFocus?: boolean;
    ariaLabel?: string;
}

/**
 * Text as the decimal it states, or null where it cannot be read at all.
 *
 * The TEXT is what publishes: "1." and "2.50" are decimals no number is, and
 * the column keeps them. A trailing dot is probed without it, because
 * `Decimal` throws rather than answering, and a throw here would take the
 * render down instead of leaving the field unready.
 */
function readDecimal(
    text: string,
    integerOnly: boolean,
): {readonly value: DecimalValue | null} | null {
    if (text === "") {
        return {value: null};
    }
    if (integerOnly && text.includes(".")) {
        return null;
    }
    if (text === ".") {
        return {value: "0."};
    }
    try {
        new Decimal(text.endsWith(".") ? text.slice(0, -1) : text);
    } catch {
        return null;
    }
    return {value: text};
}

/**
 * Stored decimals serialize padded to the column scale (numeric with
 * decimal_places=30), so "2.76" comes back as "2.760000000000000000000000000000".
 * Text this input is holding shows as typed; anything else shows trimmed.
 */
function showDecimal(value: DecimalValue | null): string {
    return value == null || value === "" ? "" : utils.removeTrailingZeros(value);
}

export const DecimalInput = (props: DecimalInputProps): React.ReactElement => {
    const integerOnly = props.integerOnly === true;
    const buffer = shared.useBuffer(props, {
        read: (text) => readDecimal(text, integerOnly),
        show: showDecimal,
    });

    return (
        <input
            name={props.name}
            id={props.name}
            aria-label={props.ariaLabel}
            className={
                props.className ??
                classNames(
                    styles.input,
                    props.readOnly === true && styles.readOnlyButStillInput,
                )
            }
            placeholder={props.placeholder ?? ""}
            inputMode={integerOnly ? "numeric" : "decimal"}
            type="text"
            value={buffer.text}
            onChange={(event) => {
                buffer.onText(event.currentTarget.value);
            }}
            readOnly={props.readOnly}
            autoFocus={props.autoFocus}
        />
    );
};

export const Suffixed = (props: {
    suffix: string;
    children?: React.ReactNode;
    readOnly?: boolean;
}) => (
    <div
        className={classNames(
            styles.inputGroup,
            props.readOnly == true && styles.inputGroupReadOnly,
        )}
    >
        {props.children}
        <span className={styles.inputGroupLabel}>{props.suffix}</span>
    </div>
);

export const PercentInput = (props: DecimalInputProps) => (
    <Suffixed suffix="%">
        <DecimalInput {...props} />
    </Suffixed>
);

interface ConversionInputProps extends DecimalInputProps {
    unit: {
        condition: boolean;
        multiplier: DecimalValue;
        suffixes: [string, string];
    };
    className?: string;
}

/**
 * A decimal typed in one unit and stored in another. The text is the displayed
 * unit, the published value is the stored one, and the round trip through
 * `useBuffer` is what keeps a half-typed "10." on screen while it publishes.
 *
 * See: http://stackoverflow.com/questions/28072727/translating-between-cents-and-dollars-in-html-input-in-react/28077112#28077112
 */
export const ConversionInput = (props: ConversionInputProps): React.ReactElement => {
    const multiplier = props.unit.condition
        ? new Decimal(props.unit.multiplier)
        : new Decimal("1");
    const suffix = props.unit.condition
        ? props.unit.suffixes[0]
        : props.unit.suffixes[1];

    const buffer = shared.useBuffer(props, {
        read: (text) => {
            const read = readDecimal(text, props.integerOnly === true);
            if (read === null || read.value === null) {
                return read;
            }
            const stated = read.value;
            const probe = stated.endsWith(".") ? stated.slice(0, -1) : stated;
            return {value: new Decimal(probe).times(multiplier).toString()};
        },
        show: (value) =>
            value == null || value === ""
                ? ""
                : new Decimal(value)
                      .dividedBy(multiplier)
                      .toDecimalPlaces(2)
                      .toString(),
    });

    const widget = (
        <>
            <input
                name={props.name}
                aria-label={props.ariaLabel}
                className={props.className ?? styles.input}
                placeholder={props.placeholder ?? ""}
                inputMode="decimal"
                type="text"
                readOnly={props.readOnly === true}
                value={buffer.text}
                onChange={(event) => {
                    buffer.onText(event.currentTarget.value);
                }}
                autoFocus={props.autoFocus}
            />
            <input type="hidden" hidden name={props.name} value={props.value ?? ""} />
        </>
    );

    if (props.className != null) {
        return widget;
    }

    return (
        <Suffixed readOnly={props.readOnly} suffix={suffix}>
            {widget}
        </Suffixed>
    );
};

interface HeightInputProps extends DecimalInputProps {
    isImperial: boolean;
}

export const LengthInput = (props: HeightInputProps) => (
    <ConversionInput
        {...props}
        unit={{
            condition: props.isImperial,
            multiplier: "2.54",
            suffixes: ["in", "cm"],
        }}
    />
);

interface WeightInputProps extends DecimalInputProps {
    isImperial: boolean;
    className?: string;
}

export const WeightInput = (props: WeightInputProps) => (
    <ConversionInput
        {...props}
        unit={{
            condition: props.isImperial,
            multiplier: "0.45359237",
            suffixes: ["lb", "kg"],
        }}
    />
);

interface WeightMultiplierInputProps extends DecimalInputProps {
    isImperial: boolean;
}

export const WeightMultiplierInput = (props: WeightMultiplierInputProps) => (
    <ConversionInput
        {...props}
        unit={{
            condition: props.isImperial,
            multiplier: new Decimal(1).dividedBy("0.45359237").toString(),
            suffixes: ["per lb", "per kg"],
        }}
    />
);

interface EnergyInputProps extends DecimalInputProps {
    useKilojoules?: boolean;
}

export const EnergyInput = (props: EnergyInputProps) => (
    <ConversionInput
        {...props}
        unit={{
            condition: props.useKilojoules === true,
            multiplier: constants.KILOJOULES_MULTIPLIER,
            suffixes: ["kj", "cal"],
        }}
    />
);

interface IntegerInputProps {
    name: string;
    value: number | null;
    placeholder?: string;
    onChange: (value: number | null) => void;
}

export class IntegerInput extends React.Component<IntegerInputProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value: string = event.currentTarget.value;
        this.props.onChange(value === "" ? null : parseInt(value, 10));
    };

    render() {
        const {value} = this.props;
        return (
            <input
                name={this.props.name}
                type="number"
                className={styles.input}
                placeholder={this.props.placeholder ?? ""}
                onChange={this.handleChange}
                value={value ?? ""}
            />
        );
    }
}

export const RangeInput = (props: DecimalInputProps): React.ReactElement => {
    // A slider only ever emits a numeral, so nothing here is ever unreadable;
    // it shares the buffer so a padded stored decimal still shows trimmed.
    const buffer = shared.useBuffer(props, {
        read: (text) => readDecimal(text, false),
        show: showDecimal,
    });

    return (
        <input
            name={props.name}
            max="1"
            min="0"
            step="0.01"
            type="range"
            className={classNames(
                styles.range,
                "[&::-webkit-slider-thumb]:appearance-none",
                "[&::-webkit-slider-thumb]:size-4",
                "[&::-webkit-slider-thumb]:rounded-lg",
                "[&::-webkit-slider-thumb]:bg-indigo-600",
                "[&::-moz-range-thumb]:appearance-none",
                "[&::-moz-range-thumb]:size-4",
                "[&::-moz-range-thumb]:rounded-lg",
                "[&::-moz-range-thumb]:bg-indigo-600",
            )}
            onChange={(event) => {
                buffer.onText(event.currentTarget.value);
            }}
            value={buffer.text}
        />
    );
};

interface TextInputProps {
    value: string | null;
    name: string;
    placeholder?: string;
    readOnly: boolean;
    flush?: boolean;
    onChange: (value: string) => void;
    spellCheck?: boolean;
    small?: boolean;
    className?: string;
    rows?: number;
    ariaLabel?: string;
}

export const TextInput = React.forwardRef(function TextInput(
    props: TextInputProps,
    ref: React.Ref<HTMLInputElement>,
) {
    const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value: string = event.currentTarget.value;
        props.onChange(value);
    };

    const input = (
        <input
            type="text"
            ref={ref}
            // Disable one password, fields named "name" trigger it in Chrome at least.
            // Investigate if properly wrapping the label helps.
            autoComplete="off"
            name={props.name}
            aria-label={props.ariaLabel}
            readOnly={props.readOnly}
            placeholder={props.placeholder ?? ""}
            data-1p-ignore={props.placeholder == "Exercise name"}
            className={
                props.className ??
                classNames(
                    styles.input,
                    props.flush === true && styles.flush,
                    props.small === true && styles.small,
                    props.readOnly === true && styles.readOnly,
                )
            }
            value={props.value ?? ""}
            onChange={handleChange}
            spellCheck={props.spellCheck ?? false}
        />
    );

    return props.flush === true ? (
        <div className={styles.flushWrapper}>
            <SimpleIcon className={styles.flushIcon} icon="0803-magnifier" />
            {input}
        </div>
    ) : (
        input
    );
});

export class PasswordInput extends React.Component<TextInputProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value: string = event.currentTarget.value;
        this.props.onChange(value);
    };

    render() {
        const {value} = this.props;
        return (
            <input
                name={this.props.name}
                type="password"
                readOnly={this.props.readOnly}
                className={this.props.className ?? styles.input}
                value={value ?? ""}
                onChange={this.handleChange}
            />
        );
    }
}

export class EmailInput extends React.Component<TextInputProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value: string = event.currentTarget.value;
        this.props.onChange(value);
    };

    render() {
        const {value} = this.props;
        return (
            <input
                name={this.props.name}
                type="email"
                placeholder={this.props.placeholder}
                readOnly={this.props.readOnly}
                className={
                    this.props.className ??
                    classNames(styles.input, this.props.readOnly && styles.readOnly)
                }
                value={value ?? ""}
                onChange={this.handleChange}
            />
        );
    }
}

export class TextArea extends React.Component<TextInputProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
        const value: string = event.currentTarget.value;
        this.props.onChange(value);
    };

    render() {
        const {value, name, readOnly, placeholder, className, rows} = this.props;

        return (
            <textarea
                name={name}
                rows={rows}
                readOnly={readOnly}
                className={
                    className ??
                    classNames(styles.textarea, readOnly && styles.readOnly)
                }
                placeholder={placeholder}
                value={value ?? ""}
                onChange={this.handleChange}
            />
        );
    }
}

export function MarkdownArea(props: TextInputProps) {
    const {name, value, onChange, placeholder, readOnly, rows, className} = props;
    const [isPreviewing, setIsPreviewing] = React.useState(false);

    return (
        <div className={classNames("relative")}>
            {!readOnly && (
                <div className="absolute top-1 right-1 z-10">
                    <button
                        type="button"
                        onClick={() => setIsPreviewing(!isPreviewing)}
                        className="rounded-sm border border-stone-300 bg-white px-2 py-0.5 text-xs shadow-xs hover:bg-stone-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                        aria-label={
                            isPreviewing
                                ? "Switch to edit mode"
                                : "Switch to preview mode"
                        }
                    >
                        {isPreviewing ? "Edit" : "Preview"}
                    </button>
                </div>
            )}
            {isPreviewing ? (
                <div className="p-2">
                    <UserMarkdown content={value ?? ""} />
                </div>
            ) : (
                <TextArea
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    rows={rows}
                    className={className}
                />
            )}
        </div>
    );
}

interface SelectProps {
    name: string;
    value: string | boolean | number | null;
    readOnly?: boolean;
    onChange: (event: React.FormEvent<HTMLSelectElement>) => void;
    children?: React.ReactNode;
    small?: boolean;
    className?: string;
    ariaLabel?: string;
}

const Select = (props: SelectProps) => {
    const {children, name, onChange, value} = props;

    if (props.className != null) {
        return (
            <select
                name={name}
                aria-label={props.ariaLabel}
                className={props.className}
                disabled={props.readOnly}
                onChange={onChange}
                // Very important that we handle null directly as 0 is different from null
                // for SelectNumber. "" is equal to null though.
                value={value != null ? value.toString() : ""}
            >
                {children}
            </select>
        );
    }

    return (
        <div className={classNames(props.readOnly !== true && styles.selectWrapper)}>
            <SimpleIcon className={styles.selectIcon} icon="custom-chevron-down" />
            <select
                name={name}
                aria-label={props.ariaLabel}
                className={classNames(
                    styles.select,
                    props.small === true && styles.small,
                    props.readOnly === true && styles.readOnly,
                )}
                disabled={props.readOnly}
                onChange={onChange}
                // Very important that we handle null directly as 0 is different from null
                // for SelectNumber. "" is equal to null though.
                value={value != null ? value.toString() : ""}
            >
                {children}
            </select>
        </div>
    );
};

interface SelectNumberProps {
    name: string;
    value: number | null;
    small?: boolean;
    readOnly?: boolean;
    onChange: (value: number | null) => void;
    children: React.ReactNode;
}

interface SelectTextProps {
    name: string;
    value: string;
    small?: boolean;
    readOnly?: boolean;
    onChange: {hack(value: string): void}["hack"];
    children: React.ReactNode;
    className?: string;
    ariaLabel?: string;
}

interface SelectBooleanProps {
    name: string;
    value: boolean;
    small?: boolean;
    readOnly?: boolean;
    onChange: (value: boolean) => void;
    children: React.ReactNode;
}

export class SelectNumber extends React.Component<SelectNumberProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        this.props.onChange(value === "" ? null : parseInt(value, 10));
    };

    render() {
        return (
            <Select {...this.props} onChange={this.handleChange}>
                {this.props.children}
            </Select>
        );
    }
}

export class SelectText extends React.Component<SelectTextProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        this.props.onChange(value);
    };

    render() {
        return (
            <Select {...this.props} onChange={this.handleChange}>
                {this.props.children}
            </Select>
        );
    }
}

export class SelectBoolean extends React.Component<SelectBooleanProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        this.props.onChange(value === "true" ? true : false);
    };

    render() {
        return (
            <Select {...this.props} onChange={this.handleChange}>
                {this.props.children}
            </Select>
        );
    }
}

interface DateInputProps {
    name: string;
    value: DateValue | null;
    onChange: (value: DateValue | null) => void;
    className?: string;
    readOnly: boolean;
    placeholder?: string;
}

export class DateInput extends React.Component<DateInputProps, unknown> {
    handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value: string = event.currentTarget.value;
        this.props.onChange(value);
    };

    render() {
        const {name, value} = this.props;

        const className =
            this.props.className ??
            classNames(
                styles.input,
                this.props.readOnly && styles.readOnlyButStillInput,
            );

        return (
            <input
                name={name}
                type="date"
                className={className}
                value={value ?? ""}
                placeholder={this.props.placeholder ?? ""}
                onChange={this.handleChange}
            />
        );
    }
}

interface QuickSelectTextProps {
    name: string;
    type: "text";
    options: ReadonlyArray<FormOption<string | null>>;
    readOnly?: boolean;
    value: string | null;
    onChange: (value: string | null) => void;
    shrinkLast: boolean | "even";
}

interface QuickSelectNumberProps {
    name: string;
    type: "number";
    options: ReadonlyArray<FormOption<number | null>>;
    readOnly?: boolean;
    value: number | null;
    onChange: (value: number | null) => void;
    shrinkLast: boolean | "even";
}

type QuickSelectProps = (QuickSelectNumberProps | QuickSelectTextProps) &
    shared.Associations;

export function QuickSelect(props: QuickSelectProps) {
    let options: ReadonlyArray<FormOption<unknown>>;
    // oxlint-disable-next-line typescript/no-explicit-any
    let onChange: (value: any) => void;

    let value = props.value;

    if (props.type === "number") {
        options = props.options;
        onChange = props.onChange;
    } else {
        // This is a mess. Because of historical reasons, ["VALUE"].toString()
        // happens to return "VALUE" and works in a regular React Select.
        // So we emulate the behavior in quick select. But only for text.
        value = value != null ? value.toString() : value;
        options = props.options;
        onChange = props.onChange;
    }

    const styleProp =
        props.shrinkLast === "even" ? {width: `${100 / options.length}%`} : {};

    // For some reason, in iOS, you cannot use buttons. You have to use anchor tags that look like buttons.
    return (
        <>
            <input type="hidden" hidden name={props.name} defaultValue={value ?? ""} />
            <div
                role="group"
                id={props.id}
                aria-label={props["aria-label"]}
                aria-labelledby={props["aria-labelledby"]}
                aria-describedby={props["aria-describedby"]}
                aria-invalid={props["aria-invalid"]}
                className={classNames(
                    styles.quickSelect,
                    props.readOnly === true && styles.quickSelectReadOnly,
                    props.shrinkLast === "even" && styles.quickSelectEven,
                )}
            >
                {options.map((option, index) => (
                    <button
                        key={index}
                        type="button"
                        style={styleProp}
                        onClick={() =>
                            props.readOnly !== true ? onChange(option.value) : () => {}
                        }
                        className={classNames(
                            styles.quickSelectOption,
                            props.shrinkLast === true && styles.shrinkLast,
                            value === option.value && styles.quickSelected,
                            props.readOnly === true && styles.quickSelectOptionDisabled,
                            value === option.value &&
                                props.readOnly === true &&
                                styles.quickSelectOptionSelectedAndDisabled,
                            "text-[12px]! sm:text-sm!",
                        )}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </>
    );
}

// The three standalone controls the forms API needs and Joy never had. Their
// prop shapes are the ones their existing callsites pass.

interface NumberInputProps {
    name: string;
    id?: string;
    value: number | null;
    onChange: (value: number | null) => void;
    integerOnly?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
    ariaLabel?: string;
}

/**
 * Text that reads publishes its number and keeps the text as typed, so "1."
 * publishes 1 and still shows the dot. Text that does not read stays on
 * screen and leaves the field unready instead of letting the previous number
 * submit.
 */
export const NumberInput = (props: NumberInputProps): React.ReactElement => {
    const integerOnly = props.integerOnly === true;
    const buffer = shared.useBuffer(props, {
        read: (text) => {
            if (text === "") {
                return {value: null};
            }
            const pattern = integerOnly ? /^-?\d+\.?$/ : /^-?\d+(\.\d+)?$/;
            return pattern.test(text) ? {value: Number.parseFloat(text)} : null;
        },
        show: (value) => (value == null ? "" : String(value)),
    });

    return (
        <input
            name={props.name}
            id={props.id}
            aria-label={props.ariaLabel}
            type="text"
            inputMode={integerOnly ? "numeric" : "decimal"}
            className={
                props.className ??
                classNames(
                    styles.input,
                    props.readOnly === true && styles.readOnlyButStillInput,
                )
            }
            placeholder={props.placeholder ?? ""}
            readOnly={props.readOnly}
            value={buffer.text}
            onChange={(event) => {
                buffer.onText(event.currentTarget.value);
            }}
        />
    );
};

interface DurationInputProps {
    name?: string;
    /** Seconds on the wire; the control splits and joins them. */
    value: SecondsValue | null;
    onChange: (value: SecondsValue | null) => void;
    format?: "hh:mm" | "hh:mm:ss" | "mm:ss";
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
    /** The h/m/s label beside each segment. */
    suffixClassName?: string;
    ariaLabel?: string;
}

/** The three segments as one string, which is what the buffer holds. */
const DURATION_SEGMENTS = ["hours", "minutes", "seconds"] as const;

function joinDuration(value: SecondsValue | null): string {
    if (value == null) {
        return "::";
    }
    // A zero segment shows blank, the way it always has.
    const unit = (count: number) => (count === 0 ? "" : String(count));
    return [
        unit(Math.floor(value / 3600)),
        unit(Math.floor((value % 3600) / 60)),
        unit(value % 60),
    ].join(":");
}

/**
 * Seconds, typed as hours, minutes and seconds.
 *
 * Every segment is digits or blank. Anything else — "1a" — stays on screen and
 * leaves the field unready, where it used to publish whatever `parseInt` made
 * of its leading digits. A zero somebody typed is the number zero; only all
 * three blank is null.
 */
export const DurationInput = (props: DurationInputProps): React.ReactElement => {
    const buffer = shared.useBuffer(props, {
        read: (text) => {
            const parts = text.split(":");
            if (parts.length !== DURATION_SEGMENTS.length) {
                return null;
            }
            if (parts.every((part) => part === "")) {
                return {value: null};
            }
            if (parts.some((part) => part !== "" && !/^\d+$/.test(part))) {
                return null;
            }
            const [hours, minutes, seconds] = parts.map((part) =>
                part === "" ? 0 : Number(part),
            );
            return {value: hours * 3600 + minutes * 60 + seconds};
        },
        show: joinDuration,
    });

    const held = buffer.text.split(":");

    const segment = (index: number, short: string, long: string) => (
        <div className="flex">
            <input
                type="text"
                inputMode="numeric"
                aria-label={`${props.ariaLabel ?? ""} ${long}`.trim()}
                className={props.className ?? styles.input}
                readOnly={props.readOnly}
                placeholder="00"
                value={held[index] ?? ""}
                onChange={(event) => {
                    const next = [...held];
                    next[index] = event.currentTarget.value;
                    buffer.onText(next.join(":"));
                }}
            />
            <span className={props.suffixClassName ?? styles.inputGroupLabel}>
                <span className="sm:hidden">{short}</span>
                <span className="max-sm:hidden">{long}</span>
            </span>
        </div>
    );

    return (
        <div className="flex gap-1 *:flex-1">
            {props.format !== "mm:ss" && segment(0, "h", "hours")}
            {segment(1, "m", "minutes")}
            {props.format !== "hh:mm" && segment(2, "s", "seconds")}
            <input type="hidden" hidden name={props.name} value={props.value ?? ""} />
        </div>
    );
};

/**
 * A select field as segmented buttons. A disabled option is left out, and a
 * field that is not required offers "None" last.
 */
export const QuickSelectField = <TValue extends shared.AnyValue>(
    props: {
        field: shared.FieldHandle<"select", TValue>;
    } & shared.Associations,
): React.ReactElement => {
    const {field, ...associations} = props;
    const asText = (value: shared.AnyValue) => (value == null ? "" : String(value));
    const options = field.options
        .filter(([value]) => !field.disabledOptions.some((d) => d === value))
        .map(([value, label]) => ({value: asText(value), label}))
        .concat(field.required ? [] : [{value: "", label: "None"}]);

    return (
        <QuickSelect
            {...associations}
            type="text"
            name={field.name}
            options={options}
            readOnly={field.readOnly}
            value={asText(field.value)}
            onChange={(chosen) => {
                const member = field.options.find(
                    ([value]) => asText(value) === (chosen ?? ""),
                );
                if (member !== undefined) {
                    field.onChange(member[0]);
                }
            }}
            shrinkLast={false}
        />
    );
};

// Generic over the domain: a select carries strings, numbers or booleans, and
// a control fixed at text would either refuse a numeric one or write text into
// it.
export const SelectField = <TValue extends shared.AnyValue>(
    props: {
        field: shared.FieldHandle<"select", TValue>;
        className?: string;
    } & shared.Associations,
): React.ReactElement => {
    const {field} = props;
    const asText = (value: shared.AnyValue) => (value == null ? "" : String(value));

    const control = (
        <select
            id={props.id}
            aria-label={props["aria-label"]}
            aria-labelledby={props["aria-labelledby"]}
            aria-describedby={props["aria-describedby"]}
            aria-invalid={props["aria-invalid"]}
            name={field.name}
            className={
                props.className ??
                classNames(styles.select, field.readOnly && styles.readOnly)
            }
            disabled={field.readOnly}
            value={asText(field.value)}
            onChange={(event) => {
                // The member, looked up in the field's own options: a DOM
                // string is not the value, and casting one would write text
                // into a numeric or boolean domain.
                const chosen = event.currentTarget.value;
                const member = field.options.find(
                    ([value]) => asText(value) === chosen,
                );
                if (member !== undefined) {
                    field.onChange(member[0]);
                }
            }}
        >
            {field.options.map(([value, label]) => (
                <option
                    key={asText(value)}
                    value={asText(value)}
                    disabled={field.disabledOptions.some(
                        (disabled) => disabled === value,
                    )}
                >
                    {label}
                </option>
            ))}
        </select>
    );

    if (props.className != null) {
        return control;
    }

    return (
        <div className={classNames(field.readOnly !== true && styles.selectWrapper)}>
            <SimpleIcon className={styles.selectIcon} icon="custom-chevron-down" />
            {control}
        </div>
    );
};
